/*
 * HFS (preview fixes 2026-09-30, A11Y-01 / REG-03 / GUIDE-01): product-page guide dialogs (Size Chart, Width Chart,
 * Width Explained, How to Measure). Replaces the guide block in assets/custom.js (the DOMContentLoaded handler that
 * fetches template[data-url] and opens #PopupModal-width-chart):
 *  - A11Y-01: the dialog name is the trigger's visible label (data-hfs-dialog-title, else its text without svg/style
 *    and aria-hidden nodes), never the icon's ".st0{...}" CSS.
 *  - REG-03: the helper page is fetched when the dialog opens, from the same origin (path + query of data-url, so an
 *    unpublished preview serves its own pages). Until it resolves the dialog body shows "Loading…" with
 *    aria-busy="true" (announced once through a polite status region outside the busy body). A failure shows a
 *    message in role="alert" and a Retry button that fetches again; focus stays in the dialog. Only successful,
 *    non-empty results are cached.
 *  - GUIDE-01: helper content is parsed in an inert document; every YouTube iframe is replaced by a light
 *    click-to-play control (pattern of snippets/hfs-youtube-facade.liquid): a button whose visible text and name is
 *    "Play video (loads YouTube): <title>", no thumbnail, a titled youtube-nocookie iframe created only on Play, and a
 *    Stop button that removes it. Play is the visitor's choice, so it is not consent-gated and no extra notice is
 *    added. Other iframes become a plain link.
 * The click listener runs in the capture phase and stops propagation for guide triggers, so the legacy custom.js
 * handler does not also run while that block still exists; data-url is moved to data-hfs-guide-url for the same
 * reason (the legacy block prefetched every helper page from the live domain on load).
 */
(function () {
  'use strict';
  if (window.HFSGuideDialog) return;

  var ID_RX = /^[A-Za-z0-9_-]{11}$/;
  // Titles of the two legacy embeds, from templates/page.shoe-size-width-videos.json (video_title settings).
  var KNOWN_TITLES = { '3pAIAL76NwY': 'Shoe Widths Explained', 'XeHoatJ8-os': 'How to Measure Your Feet at Home' };
  var PLAY_ICON = '<svg class="hfs-yt__play" viewBox="0 0 68 48" aria-hidden="true" focusable="false"><path d="M66.5 7.7a8.5 8.5 0 0 0-6-6C55.3.3 34 .3 34 .3s-21.3 0-26.5 1.4a8.5 8.5 0 0 0-6 6C.1 12.9.1 24 .1 24s0 11.1 1.4 16.3a8.5 8.5 0 0 0 6 6C12.7 47.7 34 47.7 34 47.7s21.3 0 26.5-1.4a8.5 8.5 0 0 0 6-6c1.4-5.2 1.4-16.3 1.4-16.3s0-11.1-1.4-16.3z" fill="#c40000"/><path d="M45 24 27 14v20z" fill="#fff"/></svg>';
  var CSS = [
    '#product-popup-modal__content-info .hfs-guide__loading{margin:1.5rem 0;font-size:1.6rem}',
    '#product-popup-modal__content-info .hfs-guide__error{margin:1.5rem 0 1rem;font-size:1.6rem;color:#b00020}',
    '#product-popup-modal__content-info .hfs-yt{position:relative;width:100%;aspect-ratio:16/9;background:#111;overflow:hidden}',
    '#product-popup-modal__content-info .hfs-yt__btn{position:absolute;inset:0;width:100%;height:100%;padding:0;border:0;cursor:pointer;background:#111;color:#fff;font:inherit;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:1.2rem}',
    '#product-popup-modal__content-info .hfs-yt__play{width:68px;height:48px}',
    '#product-popup-modal__content-info .hfs-yt__label{max-width:90%;padding:.4rem 1rem;background:rgba(0,0,0,.75);font-size:1.6rem;line-height:1.4;text-align:center}',
    '#product-popup-modal__content-info .hfs-yt__btn:focus-visible{outline:3px solid #fff;outline-offset:-6px;box-shadow:inset 0 0 0 6px #0f6a99}',
    '#product-popup-modal__content-info .hfs-yt iframe{position:absolute;inset:0;width:100%;height:100%;min-height:0;border:0}',
    '#product-popup-modal__content-info .hfs-yt__stop{margin:.6rem 0 1.6rem}',
    '#product-popup-modal__content-info .hfs-yt__btn[hidden],#product-popup-modal__content-info .hfs-yt__stop[hidden]{display:none}'
  ].join('\n');

  var cache = {};    // same-origin URL -> parsed content root (successful, non-empty results only)
  var inflight = {}; // same-origin URL -> pending promise
  var openToken = 0;

  function guideUrl(template) {
    var raw = template.getAttribute('data-hfs-guide-url') || template.getAttribute('data-url');
    if (!raw) return null;
    try {
      var u = new URL(raw, window.location.href);
      if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
      return u.pathname + u.search; // same origin; path and any ?view= / section parameters kept
    } catch (e) {
      return null;
    }
  }

  function visibleLabel(trigger) {
    var explicit = (trigger.getAttribute('data-hfs-dialog-title') || '').trim();
    if (explicit) return explicit;
    var copy = trigger.cloneNode(true);
    copy.querySelectorAll('svg, style, script, template, [aria-hidden="true"], [hidden]').forEach(function (n) { n.remove(); });
    return copy.textContent.replace(/\s+/g, ' ').trim() || 'Size and width guide';
  }

  function hideIcons(root) {
    (root || document).querySelectorAll('[data-content] svg').forEach(function (svg) {
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');
    });
  }

  function youtubeId(src) {
    var m = String(src || '').match(/(?:youtube(?:-nocookie)?\.com\/(?:embed\/|shorts\/|watch\?(?:[^#]*&)?v=)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
    return m ? m[1] : null;
  }

  function buildFacade(doc, id, title) {
    var player = doc.createElement('div');
    player.className = 'hfs-yt-player';
    var wrap = doc.createElement('div');
    wrap.className = 'hfs-yt';
    wrap.setAttribute('data-hfs-yt', id);
    wrap.setAttribute('data-hfs-yt-title', title);
    var play = doc.createElement('button');
    play.type = 'button';
    play.className = 'hfs-yt__btn';
    play.innerHTML = PLAY_ICON; // decorative (aria-hidden); the name comes from the visible label below
    var label = doc.createElement('span');
    label.className = 'hfs-yt__label';
    label.textContent = 'Play video (loads YouTube): ' + title;
    play.appendChild(label);
    wrap.appendChild(play);
    var stop = doc.createElement('button');
    stop.type = 'button';
    stop.className = 'hfs-yt__stop button button--secondary';
    stop.hidden = true;
    stop.textContent = 'Stop video: ' + title;
    player.append(wrap, stop);
    return player;
  }

  // Parse a helper page in an inert document (nothing loads), keep its content, drop scripts and turn embeds into
  // facades. Returns null when the page has no usable content.
  function extract(text) {
    var doc = new DOMParser().parseFromString(text, 'text/html');
    var root = doc.querySelector('.page_content') || doc.querySelector('.page.content') || doc.querySelector('.cust-video-content');
    if (!root) return null;
    root.querySelectorAll('script, noscript, link, meta, object, embed').forEach(function (n) { n.remove(); });
    // facades already rendered by snippets/hfs-youtube-facade.liquid (the preview's own video page) get the same
    // control as converted embeds; their script and privacy note are not carried into the dialog
    root.querySelectorAll('.hfs-yt-player').forEach(function (player) {
      var wrap = player.querySelector('.hfs-yt[data-hfs-yt]');
      var id = wrap && wrap.getAttribute('data-hfs-yt');
      if (!ID_RX.test(id || '')) { player.remove(); return; }
      player.replaceWith(buildFacade(doc, id, (wrap.getAttribute('data-hfs-yt-title') || '').trim() || KNOWN_TITLES[id] || 'YouTube video'));
    });
    var heading = '';
    root.querySelectorAll('h1, h2, h3, h4, h5, h6, iframe').forEach(function (el) {
      if (el.tagName !== 'IFRAME') { heading = el.textContent.replace(/\s+/g, ' ').trim(); return; }
      var src = el.getAttribute('src') || el.getAttribute('data-src') || '';
      var id = youtubeId(src);
      if (id) {
        var title = (el.getAttribute('title') || '').trim() || KNOWN_TITLES[id] || heading || 'YouTube video';
        el.replaceWith(buildFacade(doc, id, title));
        return;
      }
      var href = null;
      try { href = /^https?:$/.test(new URL(src, window.location.href).protocol) ? new URL(src, window.location.href).href : null; } catch (e) { href = null; }
      if (!href) { el.remove(); return; }
      var a = doc.createElement('a');
      a.href = href;
      a.target = '_blank';
      a.rel = 'noopener';
      a.textContent = 'Open the embedded content (external site, opens in a new tab)';
      el.replaceWith(a);
    });
    if (!root.textContent.trim() && !root.querySelector('img, table, .hfs-yt')) return null;
    return root;
  }

  function load(url) {
    if (Object.prototype.hasOwnProperty.call(cache, url)) return Promise.resolve(cache[url]);
    if (inflight[url]) return inflight[url];
    var request = Promise.resolve()
      .then(function () { return window.fetch(url, { credentials: 'same-origin' }); })
      .then(function (response) {
        if (!response || !response.ok) throw new Error('Guide request failed');
        return response.text();
      })
      .then(function (text) {
        var root = extract(text);
        if (!root) throw new Error('Guide page has no content');
        cache[url] = root;
        return root;
      });
    inflight[url] = request;
    var done = function () { delete inflight[url]; };
    request.then(done, done);
    return request;
  }

  function statusRegion(panel) {
    var region = panel.querySelector('.hfs-guide__status');
    if (!region) {
      region = document.createElement('p');
      region.className = 'hfs-guide__status visually-hidden';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      panel.appendChild(region);
    }
    return region;
  }

  function injectStyles() {
    if (document.getElementById('hfs-guide-dialog-style')) return;
    var style = document.createElement('style');
    style.id = 'hfs-guide-dialog-style';
    style.textContent = CSS;
    (document.head || document.documentElement).appendChild(style);
  }

  function stopPlayer(wrap, restoreFocus) {
    var iframe = wrap.querySelector('iframe');
    if (iframe) {
      iframe.src = 'about:blank';
      iframe.remove();
    }
    var play = wrap.querySelector('.hfs-yt__btn');
    var stop = wrap.parentElement && wrap.parentElement.querySelector('.hfs-yt__stop');
    if (stop) stop.hidden = true;
    if (play) {
      play.hidden = false;
      if (restoreFocus) play.focus();
    }
  }

  function onBoxClick(event) {
    var target = event.target && event.target.closest ? event.target : null;
    if (!target) return;
    var stop = target.closest('.hfs-yt__stop');
    if (stop) {
      var player = stop.closest('.hfs-yt-player');
      var wrapToStop = player && player.querySelector('.hfs-yt');
      if (wrapToStop) stopPlayer(wrapToStop, true);
      return;
    }
    var play = target.closest('.hfs-yt__btn');
    if (!play) return;
    var wrap = play.closest('.hfs-yt');
    var id = wrap && wrap.getAttribute('data-hfs-yt');
    if (!ID_RX.test(id || '') || wrap.querySelector('iframe')) return;
    // HFS (audit 2026-10-01, C02): one video plays at a time, in the dialog and on the page behind it.
    document.querySelectorAll('.hfs-yt[data-hfs-yt]').forEach(function (other) { if (other !== wrap) stopPlayer(other, false); });
    var iframe = document.createElement('iframe');
    iframe.title = wrap.getAttribute('data-hfs-yt-title') || 'YouTube video';
    iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen');
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('referrerpolicy', 'strict-origin');
    iframe.src = 'https://www.youtube-nocookie.com/embed/' + id + '?autoplay=1&rel=0';
    play.hidden = true;
    wrap.appendChild(iframe);
    var stopButton = wrap.parentElement.querySelector('.hfs-yt__stop');
    if (stopButton) stopButton.hidden = false;
    iframe.focus();
  }

  function open(trigger, template, box, dialog) {
    var token = ++openToken;
    var panel = dialog.querySelector('[role="dialog"]') || dialog;
    var label = visibleLabel(trigger);
    panel.setAttribute('aria-label', label);
    hideIcons(trigger.parentNode || document);
    injectStyles();
    if (!box.hfsGuideBound) {
      box.hfsGuideBound = true;
      box.addEventListener('click', onBoxClick);
    }

    // Clone inside the template's inert document, so content another script left in it never loads, swap the empty
    // <p> placeholder for the guide body, then import the clean copy.
    var copy = template.content.cloneNode(true);
    var container = copy.querySelector('#ysw-item-page-popup') || copy.lastElementChild;
    var slot = copy.ownerDocument.createElement('div');
    slot.className = 'hfs-guide__body';
    var placeholder = container && container.querySelector(':scope > p');
    if (placeholder) placeholder.replaceWith(slot);
    else (container || copy).appendChild(slot);
    var nodes = document.importNode(copy, true);
    var body = nodes.querySelector('.hfs-guide__body');
    box.replaceChildren(nodes);

    var status = statusRegion(panel);
    var url = guideUrl(template);

    function showLoading() {
      box.setAttribute('aria-busy', 'true');
      var p = document.createElement('p');
      p.className = 'hfs-guide__loading';
      p.textContent = 'Loading…';
      body.replaceChildren(p);
      status.textContent = '';
      // set after the dialog is shown so the polite region is rendered when it changes (announced once per load)
      window.setTimeout(function () {
        if (token === openToken && box.getAttribute('aria-busy') === 'true') status.textContent = 'Loading ' + label + '…';
      }, 100);
    }

    function showError(fromRetry) {
      box.removeAttribute('aria-busy');
      status.textContent = '';
      var message = document.createElement('p');
      message.className = 'hfs-guide__error';
      message.setAttribute('role', 'alert');
      message.textContent = 'Sorry, the ' + label + ' could not be loaded. Check your connection, then select Retry.';
      var retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'button button--secondary hfs-guide__retry';
      retry.textContent = 'Retry';
      retry.addEventListener('click', function () {
        if (token !== openToken) return;
        panel.focus(); // the button is about to be removed; keep focus inside the dialog
        run(true);
      });
      body.replaceChildren(message, retry);
      if (fromRetry) retry.focus();
    }

    function run(fromRetry) {
      showLoading();
      if (!url) { showError(fromRetry); return; }
      load(url).then(function (root) {
        if (token !== openToken) return;
        body.replaceChildren(document.importNode(root, true));
        box.removeAttribute('aria-busy');
        status.textContent = '';
      }, function () {
        if (token !== openToken) return;
        showError(fromRetry);
      });
    }

    run(false);
    dialog.show(trigger);
  }

  function onClick(event) {
    var trigger = event.target && event.target.closest ? event.target.closest('[data-content]') : null;
    if (!trigger) return;
    var template = null;
    try { template = document.querySelector(trigger.getAttribute('data-content')); } catch (e) { return; }
    if (!template || template.tagName !== 'TEMPLATE') return;
    var box = document.getElementById('product-popup-modal__content-info');
    var dialog = document.getElementById('PopupModal-width-chart');
    if (!box || !dialog || typeof dialog.show !== 'function') return;
    event.preventDefault();
    event.stopPropagation(); // the legacy custom.js handler on document must not rebuild the dialog
    open(trigger, template, box, dialog);
  }

  function onClosed() {
    openToken++;
    var box = document.getElementById('product-popup-modal__content-info');
    if (!box) return;
    box.replaceChildren(); // also removes a playing iframe
    box.removeAttribute('aria-busy');
    var dialog = document.getElementById('PopupModal-width-chart');
    var status = dialog && dialog.querySelector('.hfs-guide__status');
    if (status) status.textContent = '';
  }

  function prepare() {
    document.querySelectorAll('template[data-url]').forEach(function (template) {
      template.setAttribute('data-hfs-guide-url', template.getAttribute('data-url'));
      template.removeAttribute('data-url');
    });
    hideIcons(document);
  }

  document.addEventListener('click', onClick, true);
  if (document.body) document.body.addEventListener('modalClosed', onClosed);
  else document.addEventListener('DOMContentLoaded', function () { document.body.addEventListener('modalClosed', onClosed); });
  // window capture runs before any DOMContentLoaded listener on document (the legacy prefetch in custom.js)
  if (document.readyState === 'loading') window.addEventListener('DOMContentLoaded', prepare, { capture: true, once: true });
  else prepare();

  window.HFSGuideDialog = { label: visibleLabel, url: guideUrl };
})();
